import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCalendarAlt, faUser, faTag, faShare, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { blogPostsData } from '../data/blogData';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1, 
    transition: { 
      duration: 0.4,
      ease: "easeOut" 
    } 
  }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { 
      duration: 0.5, 
      ease: [0.1, 0.4, 0.2, 1] 
    } 
  }
};

const BlogPost: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const postId = parseInt(id || '1');
  
  // Find the current post
  const post = blogPostsData.find(post => post.id === postId);
  
  // Find related posts (same category, excluding current)
  const relatedPosts = blogPostsData
    .filter(p => p.category === post?.category && p.id !== postId)
    .slice(0, 3);
  
  if (!post) {
    return (
      <div className="container py-20 text-center">
        <h1 className="text-2xl font-bold">Blog post not found</h1>
        <Link to="/blog" className="text-primary hover:underline mt-4 inline-block">
          Return to blog
        </Link>
      </div>
    );
  }

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative">
        <div className="h-[400px] md:h-[500px] relative">
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/70 to-black/50 z-10"></div>
          <img 
            src={post.image} 
            alt={post.title} 
            className="w-full h-full object-cover brightness-[0.85] contrast-[0.9]"
          />
          <div className="absolute inset-0 z-20 flex items-end">
            <div className="container pb-16">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
                className="max-w-4xl"
              >
                <span className="inline-block px-4 py-1 bg-primary text-white text-sm font-medium rounded-full mb-4">
                  {post.category}
                </span>
                <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 drop-shadow-md bg-black/40 backdrop-blur-sm p-4 rounded-lg inline-block">
                  {post.title}
                </h1>
                <div className="flex items-center text-white text-sm md:text-base drop-shadow-sm">
                  <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
                  {post.date}
                  <span className="mx-3">•</span>
                  <FontAwesomeIcon icon={faUser} className="mr-2" />
                  {post.author}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Content */}
      <section className="py-16">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <motion.div 
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              className="lg:col-span-2"
            >
              <div className="prose prose-lg max-w-none">
                {post.content.map((paragraph, index) => (
                  <p key={index} className="mb-6">{paragraph}</p>
                ))}
              </div>
              
              {/* Tags */}
              <div className="flex items-center flex-wrap gap-2 mt-12 pt-6 border-t border-gray-200">
                <FontAwesomeIcon icon={faTag} className="text-gray-500 mr-2" />
                {post.tags.map((tag, index) => (
                  <span key={index} className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700">
                    {tag}
                  </span>
                ))}
              </div>
              
              {/* Share */}
              <div className="flex items-center mt-8">
                <span className="font-medium mr-4">Share:</span>
                <div className="flex space-x-3">
                  <a href="#" className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-colors">
                    <FontAwesomeIcon icon={faShare} />
                  </a>
                  <a href="#" className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-colors">
                    <FontAwesomeIcon icon={faEnvelope} />
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Sidebar */}
            <motion.div 
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="lg:col-span-1"
            >
              {/* Author Box */}
              <div className="bg-gray-50 p-6 rounded-lg mb-8">
                <h3 className="text-xl font-bold mb-4">About the Author</h3>
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 rounded-full bg-gray-200 overflow-hidden">
                    <img 
                      src={`https://ui-avatars.com/api/?name=${post.author.replace(/\s+/g, '+')}&background=random`} 
                      alt={post.author}
                      className="w-full h-full object-cover" 
                    />
                  </div>
                  <div>
                    <h4 className="font-bold">{post.author}</h4>
                    <p className="text-gray-600 text-sm">
                      Expert in {post.category} with years of experience in the recruitment industry.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Related Posts */}
              <div>
                <h3 className="text-xl font-bold mb-4">Related Articles</h3>
                <div className="space-y-4">
                  {relatedPosts.map(related => (
                    <Link 
                      key={related.id}
                      to={`/blog/${related.id}`}
                      className="flex items-start space-x-3 group"
                    >
                      <div className="w-20 h-20 rounded-md overflow-hidden flex-shrink-0 relative">
                        <img 
                          src={related.image} 
                          alt={related.title} 
                          className="w-full h-full object-cover transition duration-300 group-hover:scale-110 brightness-[0.85] contrast-[0.9]" 
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 group-hover:text-primary transition-colors">
                          {related.title}
                        </h4>
                        <p className="text-sm text-gray-500">{related.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Back to Blog */}
      <section className="pb-16">
        <div className="container">
          <Link 
            to="/blog" 
            className="inline-flex items-center text-primary hover:text-primary-dark transition-colors font-medium"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
            Back to Blog
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BlogPost; 